import { BellRing, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNotificationPermission } from "@/hooks/use-notification-permission";
import { requestNotificationPermissionFromUserGesture } from "@/lib/notifications/system-notifications";
import { cn } from "@/lib/utils";

// Per browser, not per user: the permission it asks for belongs to this browser profile + origin.
const DISMISSED_STORAGE_KEY = "healo-notifications-prompt-dismissed";

function readDismissed() {
  try {
    return window.localStorage.getItem(DISMISSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, "1");
  } catch {
    // Storage unavailable: the prompt simply returns on the next load.
  }
}

/**
 * Web mode's route to the browser's permission prompt, shared with the Settings ▸ Preferences
 * Notifications card. Call it straight from a click handler — the request must run inside the
 * click (see requestNotificationPermissionFromUserGesture). Reports the outcome as a toast.
 */
export function enableNotificationsFromClick() {
  return requestNotificationPermissionFromUserGesture().then((state) => {
    if (state === "granted") {
      toast.success("Notifications are on for this browser.");
    } else if (state === "denied") {
      toast.info("Notifications are blocked for this site. Allow them in your browser's site settings.");
    } else if (state === "default") {
      toast.info("Notifications are still off. You can enable them later in Settings ▸ Preferences.");
    }
    return state;
  });
}

/**
 * Web-only nudge in the sidebar footer (the desktop app's Update-banner slot): browsers only show
 * the permission prompt after a click, so without this almost nobody would get notifications.
 * Shown while the permission is still undecided and the user hasn't dismissed it on this browser.
 * Deliberately never disabled while the request is pending — Chrome's quiet permission UI can
 * leave it unresolved indefinitely.
 */
function EnableNotificationsPrompt({ collapsed = false, className }) {
  const permission = useNotificationPermission();
  const [dismissed, setDismissed] = useState(readDismissed);

  if (permission !== "default" || dismissed) {
    return null;
  }

  const handleEnable = () => {
    void enableNotificationsFromClick();
  };

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Turn on notifications"
              className={cn(
                "flex w-full items-center justify-center rounded-md border border-border bg-card p-2 text-primary transition-colors hover:bg-accent",
                className,
              )}
              onClick={handleEnable}
            >
              <BellRing className="h-4 w-4" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">Turn on notifications for new messages</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div
      role="region"
      aria-label="Turn on notifications"
      className={cn("rounded-md border border-border bg-card px-2.5 py-2", className)}
    >
      <div className="flex items-start gap-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
          <BellRing className="h-3.5 w-3.5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <p className="text-xs font-semibold text-foreground">Turn on notifications</p>
          <p className="text-[10px] text-muted-foreground">Get alerts for new messages.</p>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          title="Dismiss"
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          onClick={handleDismiss}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <Button type="button" size="ssm" className="mt-2 w-full" onClick={handleEnable}>
        Enable notifications
      </Button>
    </div>
  );
}

export default EnableNotificationsPrompt;
